import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { applyUserLanguage } from '../i18n';
import type {
  WaSuggestionSlide,
  WaSuggestionsContentResponse,
  WaSuggestionsSelectRequest,
} from '@dreamai/types';
import { ReactComponent as DreamLogo } from '../icons/dream-onboarding-logo.svg';
import PublicNoticePage from './common/PublicNoticePage';
import './wa-suggestions-page.scss';

const IDLE_FLUSH_MS = 10_000;
const BLUR_FLUSH_MS = 5_000;
const TOAST_MS = 2_400;

const TAG_ICONS: Record<string, React.ReactNode> = {
  calendar: (
    <>
      <rect x='3' y='4' width='18' height='18' rx='2' />
      <path d='M16 2v4M8 2v4M3 10h18' />
    </>
  ),
  utensils: (
    <>
      <path d='M3 2v7c0 1.1.9 2 2 2h4a2 2 0 0 0 2-2V2' />
      <path d='M7 2v20' />
      <path d='M21 15V2a5 5 0 0 0-5 5v6c0 1.1.9 2 2 2h3Zm0 0v7' />
    </>
  ),
  plane: (
    <path d='M17.8 19.2 16 11l3.5-3.5C21 6 21.5 4 21 3c-1-.5-3 0-4.5 1.5L13 8 4.8 6.2c-.5-.1-.9.1-1.1.5l-.3.5c-.2.5-.1 1 .3 1.3L9 12l-2 3H4l-1 1 3 2 2 3 1-1v-3l3-2 3.5 5.3c.3.4.8.5 1.3.3l.5-.2c.4-.3.6-.7.5-1.2z' />
  ),
  filetext: (
    <>
      <path d='M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z' />
      <path d='M14 2v4a2 2 0 0 0 2 2h4' />
      <path d='M16 13H8' />
      <path d='M16 17H8' />
      <path d='M10 9H8' />
    </>
  ),
  gift: (
    <>
      <rect x='3' y='8' width='18' height='4' rx='1' />
      <path d='M12 8v13' />
      <path d='M19 12v7a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2v-7' />
      <path d='M7.5 8a2.5 2.5 0 0 1 0-5C11 3 12 8 12 8' />
      <path d='M16.5 8a2.5 2.5 0 0 0 0-5C13 3 12 8 12 8' />
    </>
  ),
  headset: (
    <>
      <path d='M3 11h3a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-5Z' />
      <path d='M21 11h-3a2 2 0 0 0-2 2v3a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-5Z' />
      <path d='M3 11v-1a9 9 0 0 1 18 0v1' />
    </>
  ),
};

const ACCENTS: Record<string, { dot: string; tile: string; glows: string[] }> =
  {
    blue: {
      dot: '#5FB8F5',
      tile: 'rgba(151,213,252,.38)',
      glows: ['wa-suggestions__glow--blue-1', 'wa-suggestions__glow--pink-3'],
    },
    pink: {
      dot: '#F2778F',
      tile: 'rgba(247,161,179,.38)',
      glows: ['wa-suggestions__glow--pink-1', 'wa-suggestions__glow--blue-3'],
    },
    yellow: {
      dot: '#E8C400',
      tile: 'rgba(255,214,1,.30)',
      glows: ['wa-suggestions__glow--yellow-1', 'wa-suggestions__glow--pink-3'],
    },
    navy: {
      dot: '#151C28',
      tile: 'rgba(151,213,252,.24)',
      glows: ['wa-suggestions__glow--blue-2', 'wa-suggestions__glow--yellow-3'],
    },
  };

const CheckIcon = () => (
  <svg viewBox='0 0 24 24' aria-hidden='true'>
    <path d='M20 6 9 17l-5-5' />
  </svg>
);

const ChevronDown = () => (
  <svg viewBox='0 0 24 24' aria-hidden='true'>
    <path d='m6 9 6 6 6-6' />
  </svg>
);

type PageState = 'loading' | 'ready' | 'expired' | 'invalid' | 'error';

const WaSuggestionsPage: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const apiBase = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');

  const [pageState, setPageState] = useState<PageState>('loading');
  const [slides, setSlides] = useState<WaSuggestionSlide[]>([]);
  const [assistantName, setAssistantName] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [activeIndex, setActiveIndex] = useState(0);
  const [toast, setToast] = useState<string | null>(null);

  const deckRef = useRef<HTMLDivElement>(null);
  const slideRefs = useRef<Array<HTMLElement | null>>([]);
  const toastTimer = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );
  const flushTimer = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );

  // Refs mirroring state for use inside stable listeners.
  const selectedRef = useRef(selected);
  selectedRef.current = selected;
  const syncedRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!token) {
      setPageState('invalid');
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const response = await fetch(
          `${apiBase}/wa-suggestions/content?token=${encodeURIComponent(token)}`,
        );
        const payload: WaSuggestionsContentResponse | null = await response
          .json()
          .catch(() => null);
        if (cancelled) return;
        if (response.status === 410) {
          setPageState('expired');
          return;
        }
        if (!response.ok || !payload?.data) {
          setPageState(response.status === 404 ? 'invalid' : 'error');
          return;
        }
        applyUserLanguage(
          (payload.data as { language?: string | null }).language,
        );
        setSlides(payload.data.slides);
        setAssistantName(payload.data.assistantName);
        const already = new Set(payload.data.selectedSlideIds);
        setSelected(already);
        syncedRef.current = new Set(already);
        setPageState('ready');
      } catch {
        if (!cancelled) setPageState('error');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [apiBase, token]);

  const hasUnsynced = useCallback(() => {
    const synced = syncedRef.current;
    return Array.from(selectedRef.current).some((id) => !synced.has(id));
  }, []);

  const flushSelections = useCallback(
    (unloading: boolean) => {
      if (!token || !hasUnsynced()) return;
      const slideIds = Array.from(selectedRef.current);
      const body: WaSuggestionsSelectRequest = { token, slideIds };
      syncedRef.current = new Set(slideIds);
      const url = `${apiBase}/wa-suggestions/select`;
      // keepalive lets the request survive pagehide/unload (a CORS-safe
      // sendBeacon equivalent that still carries a JSON content type).
      fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
        keepalive: true,
      }).catch(() => {
        if (unloading) return;
        // Re-mark as unsynced so a later flush retries.
        syncedRef.current = new Set(
          Array.from(syncedRef.current).filter((id) => !slideIds.includes(id)),
        );
      });
    },
    [apiBase, token, hasUnsynced],
  );

  // Debounced flush: fires after 10s of inactivity while the page is open,
  // or 5s after the page loses focus / is hidden. Messages never arrive
  // while the user is actively browsing.
  const scheduleFlush = useCallback(
    (delayMs: number) => {
      clearTimeout(flushTimer.current);
      if (!hasUnsynced()) return;
      flushTimer.current = setTimeout(() => flushSelections(false), delayMs);
    },
    [flushSelections, hasUnsynced],
  );

  useEffect(() => {
    const onActivity = () => scheduleFlush(IDLE_FLUSH_MS);
    const onBlur = () => scheduleFlush(BLUR_FLUSH_MS);
    const onFocus = () => scheduleFlush(IDLE_FLUSH_MS);
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        clearTimeout(flushTimer.current);
        flushSelections(true);
      }
    };
    const onPageHide = () => {
      clearTimeout(flushTimer.current);
      flushSelections(true);
    };
    window.addEventListener('pointerdown', onActivity);
    window.addEventListener('touchmove', onActivity);
    window.addEventListener('keydown', onActivity);
    window.addEventListener('blur', onBlur);
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', onPageHide);
    const deck = deckRef.current;
    deck?.addEventListener('scroll', onActivity, { passive: true });
    return () => {
      window.removeEventListener('pointerdown', onActivity);
      window.removeEventListener('touchmove', onActivity);
      window.removeEventListener('keydown', onActivity);
      window.removeEventListener('blur', onBlur);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', onPageHide);
      deck?.removeEventListener('scroll', onActivity);
      clearTimeout(flushTimer.current);
    };
  }, [scheduleFlush, flushSelections]);

  // Track the active slide for the progress bar.
  useEffect(() => {
    if (pageState !== 'ready') return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const idx = slideRefs.current.indexOf(entry.target as HTMLElement);
            if (idx >= 0) setActiveIndex(idx);
          }
        });
      },
      { root: deckRef.current, threshold: 0.6 },
    );
    slideRefs.current.forEach((el) => el && observer.observe(el));
    return () => observer.disconnect();
  }, [pageState, slides]);

  const showToast = (text: string) => {
    setToast(text);
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), TOAST_MS);
  };

  const handleSelect = (slide: WaSuggestionSlide) => {
    const next = new Set(selectedRef.current);
    next.add(slide.slideId);
    selectedRef.current = next;
    setSelected(next);
    showToast(slide.toastText);
    scheduleFlush(IDLE_FLUSH_MS);
  };

  const totalSlides = slides.length + 1; // + cover
  const segments = useMemo(
    () => Array.from({ length: totalSlides }, (_, i) => i),
    [totalSlides],
  );

  if (pageState === 'loading') {
    return <PublicNoticePage loading />;
  }

  if (pageState !== 'ready') {
    const copy =
      pageState === 'expired'
        ? t('suggestions.expiredBody')
        : pageState === 'invalid'
          ? t('suggestions.invalidBody')
          : t('suggestions.errorBody');
    return (
      <PublicNoticePage
        title={
          pageState === 'expired'
            ? t('suggestions.linkExpiredTitle')
            : t('suggestions.somethingOffTitle')
        }
        body={copy}
      />
    );
  }

  return (
    <div className='wa-suggestions'>
      <div className='wa-suggestions__progress'>
        {segments.map((i) => (
          <span
            key={i}
            className={
              i === activeIndex ? 'active' : i < activeIndex ? 'done' : ''
            }
          />
        ))}
      </div>

      <div className='wa-suggestions__deck' ref={deckRef}>
        <section
          className='wa-suggestions__slide wa-suggestions__slide--cover'
          ref={(el) => {
            slideRefs.current[0] = el;
          }}
        >
          <div className='wa-suggestions__glow wa-suggestions__glow--yellow-1' />
          <div className='wa-suggestions__glow wa-suggestions__glow--pink-2' />
          <div className='wa-suggestions__glow wa-suggestions__glow--blue-3' />
          <div className='wa-suggestions__slide-inner'>
            <div className='wa-suggestions__content'>
              <DreamLogo className='wa-suggestions__logo' />
              <div className='wa-suggestions__pill'>
                <span className='wa-suggestions__pill-avatar' />
                {assistantName
                  ? t('suggestions.fromAssistantNamed', { name: assistantName })
                  : t('suggestions.fromAssistant')}
              </div>
              <h1>
                {t('suggestions.titleLead')}{' '}
                <span className='wa-suggestions__grad'>
                  {t('suggestions.titleAccent')}
                </span>
              </h1>
              <p className='wa-suggestions__copy'>
                {t('suggestions.coverCopy')}
              </p>
            </div>
            <div className='wa-suggestions__swipe'>
              {t('suggestions.swipeUp')}
              <ChevronDown />
            </div>
          </div>
        </section>

        {slides.map((slide, i) => {
          const accent = ACCENTS[slide.accent] || ACCENTS.blue;
          const isSelected = selected.has(slide.slideId);
          const number = String(i + 1).padStart(2, '0');
          return (
            <section
              key={slide.slideId}
              className='wa-suggestions__slide'
              ref={(el) => {
                slideRefs.current[i + 1] = el;
              }}
            >
              <div className={`wa-suggestions__glow ${accent.glows[0]}`} />
              <div className={`wa-suggestions__glow ${accent.glows[1]}`} />
              <div className='wa-suggestions__slide-inner'>
                <div className='wa-suggestions__watermark'>{number}</div>
                <div className='wa-suggestions__topline'>
                  <span className='wa-suggestions__tag'>
                    <svg viewBox='0 0 24 24' aria-hidden='true'>
                      {TAG_ICONS[slide.icon]}
                    </svg>
                    {slide.tag}
                  </span>
                </div>
                <div className='wa-suggestions__content'>
                  <div className='wa-suggestions__lede'>
                    <h2>
                      {slide.titleSegments.map((seg, j) =>
                        seg.gradient ? (
                          <span key={j} className='wa-suggestions__grad'>
                            {seg.text}
                          </span>
                        ) : (
                          <React.Fragment key={j}>{seg.text}</React.Fragment>
                        ),
                      )}
                    </h2>
                  </div>
                  <div className='wa-suggestions__bullets'>
                    {slide.bullets.map((bullet, j) => (
                      <div key={j} className='wa-suggestions__bullet'>
                        <span
                          className='wa-suggestions__bullet-icon'
                          style={{ background: accent.tile }}
                        >
                          <svg
                            viewBox='0 0 24 24'
                            style={{ stroke: accent.dot }}
                            aria-hidden='true'
                          >
                            <path d='M20 6 9 17l-5-5' />
                          </svg>
                        </span>
                        <span>{bullet}</span>
                      </div>
                    ))}
                  </div>
                  <div className='wa-suggestions__cta-row'>
                    <button
                      type='button'
                      className={`wa-suggestions__cta${
                        isSelected ? ' wa-suggestions__cta--selected' : ''
                      }`}
                      onClick={() => handleSelect(slide)}
                      disabled={isSelected}
                    >
                      {isSelected ? (
                        <>
                          <CheckIcon />
                          {t('suggestions.picked')}
                        </>
                      ) : (
                        <>
                          {slide.ctaLabel}
                          <svg viewBox='0 0 24 24' aria-hidden='true'>
                            <path d='M5 12h14M12 5l7 7-7 7' />
                          </svg>
                        </>
                      )}
                    </button>
                  </div>
                </div>
                <div className='wa-suggestions__foot'>
                  <DreamLogo className='wa-suggestions__wordmark' />
                  <span className='wa-suggestions__hint'>
                    {i < slides.length - 1 ? (
                      <>
                        {t('suggestions.swipeNext')}
                        <ChevronDown />
                      </>
                    ) : (
                      <>{t('suggestions.thatsTheLot')}</>
                    )}
                  </span>
                </div>
              </div>
            </section>
          );
        })}
      </div>

      <div className={`wa-suggestions__toast${toast ? ' show' : ''}`}>
        <span className='wa-suggestions__toast-tick'>
          <CheckIcon />
        </span>
        <span>{toast}</span>
      </div>
    </div>
  );
};

export default WaSuggestionsPage;
